import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store';
import { ROUTES } from '../../constants';
import { PAGE_LAYOUT } from '../../constants/app.constants';
import { LENDER_STATUS } from '../../constants/clients.constants';
import { clientsService } from '../../services/clients.service';
import { lendersService } from '../../services/lenders.service';
import { lenderPackagesService } from '../../services/lender-packages.service';
import { ClientDetail, LenderDetail } from '../../types/clients.types';
import { LenderPackageResponse } from '../../types/lender-packages.types';
import { ConfirmationModal, ConfirmationType } from '../../components/shared/ui/ConfirmationModal';
import { CreateLenderModal } from './components/CreateLenderModal';
import { ClientCatalogImportModal } from './components/ClientCatalogImportModal';
import { LenderPackageImportModal } from './components/LenderPackageImportModal';

export const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authUser = useAppSelector((state) => state.auth.user);
  const isAdmin = authUser?.role === 'superadmin' || authUser?.role === 'admin';

  const [client, setClient] = useState<ClientDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddLenderOpen, setIsAddLenderOpen] = useState(false);
  const [lenderToRemove, setLenderToRemove] = useState<LenderDetail | null>(null);
  const [isCatalogImportOpen, setIsCatalogImportOpen] = useState(false);
  const [lenderToImport, setLenderToImport] = useState<LenderDetail | null>(null);

  const fetchClient = useCallback(async (): Promise<void> => {
    if (!id) return;

    try {
      const data = await clientsService.getById(id);
      setClient(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load client';
      toast.error(message);
      navigate(ROUTES.CLIENTS);
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const handleCreateLender = async (name: string): Promise<void> => {
    if (!id) return;

    await lendersService.create({ name, clientId: id });
    toast.success(`Lender "${name}" added`);
    await fetchClient();
  };

  const handleToggleStatus = async (lender: LenderDetail): Promise<void> => {
    const newStatus = lender.status === LENDER_STATUS.ACTIVE ? LENDER_STATUS.INACTIVE : LENDER_STATUS.ACTIVE;

    try {
      await lendersService.updateStatus(lender.id, newStatus);
      toast.success(`${lender.name} ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      await fetchClient();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update status';
      toast.error(message);
    }
  };

  const handleRemoveLender = async (): Promise<void> => {
    if (!id || !lenderToRemove) return;

    try {
      await clientsService.removeLender(id, lenderToRemove.id);
      toast.success(`Lender "${lenderToRemove.name}" removed`);
      setLenderToRemove(null);
      await fetchClient();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove lender';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm">Loading client...</span>
        </div>
      </div>
    );
  }

  if (!client) return null;

  const sortedLenders = [...client.lenders].sort((a, b) => a.name.localeCompare(b.name));
  const activeLenderCount = sortedLenders.filter((l) => l.status === LENDER_STATUS.ACTIVE).length;

  return (
    <div className={PAGE_LAYOUT.PAGE_WRAPPER}>
      <div className={PAGE_LAYOUT.WIDE_CONTAINER_NO_VERTICAL}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to={ROUTES.CLIENTS} className="hover:text-slate-700 transition-colors">
            Clients
          </Link>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-slate-900 font-medium">{client.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">{client.name}</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {activeLenderCount} active lender{activeLenderCount !== 1 ? 's' : ''}
                {sortedLenders.length > activeLenderCount && (
                  <span className="text-slate-400"> ({sortedLenders.length} total)</span>
                )}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsCatalogImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import Client CSV Data
          </button>
        </div>

        <hr className="border-slate-200 mb-8" />

        {/* Lenders section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-900">Lenders</h2>
            {isAdmin && (
              <button
                onClick={() => setIsAddLenderOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Lender
              </button>
            )}
          </div>

          {sortedLenders.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200/80 p-8 text-center">
              <p className="text-sm text-slate-400 italic">No lenders associated yet.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {sortedLenders.map((lender) => (
                <LenderRow
                  key={lender.id}
                  lender={lender}
                  isAdmin={isAdmin}
                  onImport={() => setLenderToImport(lender)}
                  onToggleStatus={() => handleToggleStatus(lender)}
                  onRemove={() => setLenderToRemove(lender)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateLenderModal
        isOpen={isAddLenderOpen}
        clientName={client.name}
        onClose={() => setIsAddLenderOpen(false)}
        onSubmit={handleCreateLender}
      />
      <ConfirmationModal
        isOpen={lenderToRemove !== null}
        onClose={() => setLenderToRemove(null)}
        onConfirm={handleRemoveLender}
        type={ConfirmationType.DANGER}
        title={`Remove "${lenderToRemove?.name}"?`}
        message={
          <>
            This will permanently remove <span className="font-semibold">{lenderToRemove?.name}</span> from{' '}
            <span className="font-semibold">{client.name}</span>. To temporarily hide a lender, use the disable toggle instead.
          </>
        }
        confirmButtonText="Remove Lender"
        cancelButtonText="Cancel"
      />
      <ClientCatalogImportModal
        isOpen={isCatalogImportOpen}
        clientId={client.id}
        clientName={client.name}
        onClose={() => setIsCatalogImportOpen(false)}
      />
      <LenderPackageImportModal
        isOpen={lenderToImport !== null}
        lenderId={lenderToImport?.id ?? ''}
        lenderName={lenderToImport?.name ?? ''}
        onClose={() => setLenderToImport(null)}
      />
    </div>
  );
};

interface LenderRowProps {
  lender: LenderDetail;
  isAdmin: boolean;
  onImport: () => void;
  onToggleStatus: () => void;
  onRemove: () => void;
}

const LenderRow: React.FC<LenderRowProps> = ({ lender, isAdmin, onImport, onToggleStatus, onRemove }) => {
  const isActive = lender.status === LENDER_STATUS.ACTIVE;
  const [isExpanded, setIsExpanded] = useState(false);
  const [packages, setPackages] = useState<LenderPackageResponse[] | null>(null);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  const handleToggleExpand = async (): Promise<void> => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (!next) return;

    // Always refetch on expand so the list reflects the latest imports.
    setIsLoadingPackages(true);
    try {
      const data = await lenderPackagesService.getPackagesByLender(lender.id);
      setPackages(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load packages');
      setPackages([]);
    } finally {
      setIsLoadingPackages(false);
    }
  };

  const handleToggleEnphase = async (pkg: LenderPackageResponse): Promise<void> => {
    const nextValue = !pkg.auto_enphase_accessories;
    try {
      await lenderPackagesService.updateAutoEnphaseAccessories(pkg.id, nextValue);
      setPackages(prev =>
        prev?.map(p => (p.id === pkg.id ? { ...p, auto_enphase_accessories: nextValue } : p)) ?? prev,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update package');
    }
  };

  return (
    <div className={`rounded-xl border transition-colors ${
      isActive
        ? 'bg-white border-slate-200/80 hover:border-slate-300'
        : 'bg-slate-50/50 border-slate-200/60 opacity-60'
    }`}>
      <div className="flex items-center justify-between px-4 py-3">
      <button
        onClick={handleToggleExpand}
        className="flex items-center gap-2.5 min-w-0 text-left group"
        title={isExpanded ? 'Hide packages' : 'Show packages'}
      >
        <svg
          className={`w-3.5 h-3.5 flex-shrink-0 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${
          isActive
            ? 'bg-amber-50 border-amber-100'
            : 'bg-slate-100 border-slate-200'
        }`}>
          <svg className={`w-4 h-4 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <span className={`text-sm font-medium truncate group-hover:text-slate-900 ${isActive ? 'text-slate-800' : 'text-slate-500'}`}>
          {lender.name}
        </span>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
          isActive
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-slate-100 text-slate-500 border-slate-200'
        }`}>
          {isActive ? 'active' : 'disabled'}
        </span>
      </button>

      <div className="flex items-center gap-2">
        {isActive && (
          <button
            onClick={onImport}
            className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
            title="Import lender CSV data"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import CSV Data
          </button>
        )}
        {isAdmin && (
          <>
            {isActive && <div className="w-px h-5 bg-slate-200" />}
            <button
              onClick={onToggleStatus}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                isActive ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
              title={isActive ? 'Disable lender' : 'Enable lender'}
            >
              <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                isActive ? 'translate-x-4' : 'translate-x-0.5'
              }`} />
            </button>
            <button
              onClick={onRemove}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
              title="Remove lender from client"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          {isLoadingPackages ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Loading packages...
            </div>
          ) : !packages || packages.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No packages imported for this lender yet.</p>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {packages.length} package{packages.length !== 1 ? 's' : ''}
                </p>
                <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 pr-1">
                  <svg className="w-3 h-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Enphase items
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                {packages.map((pkg, idx) => (
                  <div
                    key={pkg.id}
                    className={`flex items-center gap-3 px-3 py-1.5 border-b border-slate-100/80 last:border-b-0 transition-colors ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    } hover:bg-orange-50/30`}
                  >
                    <span className="flex-1 truncate text-xs text-slate-700">{pkg.name}</span>
                    {isAdmin ? (
                      <button
                        onClick={() => handleToggleEnphase(pkg)}
                        className={`relative inline-flex h-4 w-7 flex-shrink-0 items-center rounded-full transition-colors ${
                          pkg.auto_enphase_accessories ? 'bg-orange-500' : 'bg-slate-200'
                        }`}
                        title={pkg.auto_enphase_accessories
                          ? 'Disable Enphase accessory auto-add for this package'
                          : 'Enable Enphase accessory auto-add for this package'}
                      >
                        <span className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                          pkg.auto_enphase_accessories ? 'translate-x-3.5' : 'translate-x-0.5'
                        }`} />
                      </button>
                    ) : (
                      <svg
                        className={`w-3.5 h-3.5 flex-shrink-0 ${pkg.auto_enphase_accessories ? 'text-orange-500' : 'text-slate-200'}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
